<template>
  <div>
    <ActionableInsight
      :tabs="tabs"
      :documents="documents"
      :statements="statements"
      :table-label="`Non-Brand Compliant Live ${formattedDocumentType}s`"
      :disable-share="isSharedPage || documentType === 'template'"
      :document-type="documentType"
      :is-shared-page="isSharedPage"
    >
      <template #noResultsFoundText>
        <h2 class="text-h2">
          Great Work!
        </h2>
        <p class="employer-brand-no-result-content">
          It looks like your open job listings are compliant with your employer brand guidelines!
        </p>
      </template>
      <template #legend>
        <ActionableInsightLegend :items="legendItems" />
      </template>
    </ActionableInsight>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { startCase } from 'lodash-es';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import { useInsights } from '@/stores/insights/insights';
import ActionableInsight from '@/components/insights/ActionableInsight/ActionableInsight.vue';
import ActionableInsightLegend from '@/components/insights/ActionableInsight/ActionableInsightLegend.vue';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'EmployerBrandReport',
  components: {
    ActionableInsight,
    ActionableInsightLegend,
  },
  emits: ['track-page-load', 'insights-tracking-event'],
  data() {
    return {
      isSharedPage: false,
    };
  },
  computed: {
    ...mapState(useActionableInsights, ['documents', 'totals', 'exclusions', 'documentType']),
    ...mapState(useFilters, ['searchTerm', 'mergedTempFilters', 'myJobsEnabled']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    ...mapState(useAppAuthData, ['authData', 'isAdmin']),
    ...mapState(useDatePicker, ['dateRange']),
    formattedDocumentType() {
      return startCase(this.documentType);
    },
    legendItems() {
      return [
        {
          mood: 'happy',
          description: this.exclusions?.canUseEVPCompliance
            ? `indicates that the ${this.documentType} is complete, is not too long, is optimized for job boards or is EVP complaint`
            : `indicates that the ${this.documentType} is complete, is not too long, is optimized for job boards or has a clear workplace location.`,
        },
        {
          mood: 'sad',
          description: this.exclusions?.canUseEVPCompliance
            ? `indicates that the ${this.documentType} is incomplete, is too long, is not optimized for job boards or is not EVP complaint`
            : `indicates that the ${this.documentType} is incomplete, is too long, is not optimized for job boards or does not have a clear workplace location.`,
        },
      ];
    },
    statements() {
      const statements = [
        {
          text: 'Incomplete',
          align: 'center',
          value: 'issues.incomplete',
          class: 'missing-statement-header',
        },
        {
          text: 'Length',
          align: 'center',
          value: 'issues.long',
          class: 'missing-statement-header',
        },
        {
          text: 'Optimization',
          align: 'center',
          value: 'issues.jobBoardUnoptimized',
          class: 'missing-statement-header',
        },
      ];

      if (this.exclusions?.canUseEVPCompliance) {
        statements.push({
          text: 'EVP',
          align: 'center',
          value: 'issues.nonCompliantEVP',
          class: 'missing-statement-header',
        });
      } else {
        statements.push({
          text: 'Location',
          align: 'center',
          value: 'issues.unclearLocation',
          class: 'missing-statement-header',
        });
      }

      return statements;
    },
    tabLabels() {
      const tabLabels = [
        {
          label: 'Job Composition',
          key: 'percentageIncomplete',
          description: `of your live ${this.documentType}s are incomplete.`,
          tooltipKey: 'easy-wins-incomplete-jobs',
        },
        {
          label: 'Job Length',
          key: 'percentageLong',
          description: `of your live ${this.documentType}s exceed the suggested length of a job post.`,
          tooltipKey: 'easy-wins-long-jobs',
        },
        {
          label: 'Job Board Optimization',
          key: 'percentageJobBoardUnoptimized',
          description: `of your live ${this.documentType}s are not fully optimized for job boards like LinkedIn and Indeed.`,
          tooltipKey: 'employer-brand-job-board',
          exclusionKey: 'jobBoardUnoptimized',
          exclusionDescription: 'Your organization\'s Applicant Tracking System optimizes your posts outside of the JobNav system, and so we cannot measure performance.',
        },
      ];

      if (this.exclusions?.canUseEVPCompliance) {
        tabLabels.push({
          label: 'Employer Brand Compliance',
          key: 'percentageNonCompliantEVP',
          description: `of your live ${this.documentType}s are not compliant with your company's Employee Value Proposition (EVP).`,
          tooltipKey: 'employer-brand-evp',
        });
      } else {
        tabLabels.push({
          label: 'Workplace Type',
          key: 'percentageUnclearLocation',
          description: `of your live ${this.documentType}s are missing workplace type.`,
          tooltipKey: 'employer-brand-location',
        });
      }

      return tabLabels;
    },
    tabs() {
      return this.tabLabels.map((tab) => {
        const value = this.totals[tab.key];

        if (value === undefined) {
          return {
            ...tab,
            description: 'No data available',
            value: 0,
            disabled: true,
          };
        }

        return {
          ...tab,
          description: tab.description,
          // Will show as <1% when percentage is less than 1% and greater than 0%
          value: value < 1 && value > 0 ? '<1' : Math.round(value),
        };
      });
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadPage);

    this.setFilterConfig({
      module: 'insights',
      datepicker: true,
      narrow: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: 'chip',
    });

    this.$emit('track-page-load', { page: 'Employer Brand Report' });

    const isSharedPage = window.sessionStorage.reportingShareKey && !this.authData.isLoggedIn;

    this.isSharedPage = Boolean(isSharedPage);

    if (isSharedPage) {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });
      this.resetExportConfig();
      this.resetShareConfig();
    } else {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });

      this.setExportConfig({
        csvExport: true,
        pngExport: false,
        pngWidgetOptions: {},
        csvOptions: [
          {
            label: 'Data Table (.csv)',
            csvDownloadFunction: () => {
              this.downloadEmployerBrandReport({
                filters: this.mergedTempFilters,
                dateRange: this.dateRange,
                myJobsEnabled: this.myJobsEnabled,
                document,
                sort: this.$route.query?.sort || this.selectedSubMenuOption,
              });
            },
            waitTime: 5000,
          },
        ],
      });

      this.setShareConfig({
        shareable: true,
      });
    }

    if (this.$route.query?.templates && this.isAdmin) {
      this.toggleDocumentType('template');
    }

    await this.loadPage({
      routeQuery: this.$route.query || '',
      router: this.$router,
      page: this.$route.query?.page || 1,
      sort: this.$route.query.sort,
      firstLoad: true,
      selectedMyJobs: this.$route.query?.owned === 'true',
      searchTerm: this.searchTerm,
      selectedSubMenuOption: this.selectedSubMenuOption,
    });
  },
  beforeUnmount() {
    unsubscribeToFilterUpdates();

    // Reset table reports data
    this.setActionableInsightsData();

    // Clean up filter config and selected sub menu option
    this.setFilterConfig({
      subMenuType: null,
    });

    this.resetExportConfig();
    this.resetShareConfig();

    this.setSelectedSubMenuOption(null);

    this.toggleDocumentType('job');
  },
  methods: {
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['loadPage']),
    ...mapActions(useActionableInsights, ['setActionableInsightsData', 'downloadEmployerBrandReport', 'toggleDocumentType']),
    ...mapActions(useFilters, ['setFilterConfig']),
    ...mapActions(useSubMenu, ['setSelectedSubMenuOption']),
    ...mapActions(useInsightsExport, ['setExportConfig', 'resetExportConfig']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
  },
};
</script>

<style lang="less" scoped>
.employer-brand-no-result-content {
  max-width: 381px;
}
</style>
